import { CTA_SOURCES, PAGE_SECTIONS } from "@punchup/analytics";
import { ShowCard, ShowRow, ShowRowProvider } from "@/components/shows";
import { ShowInfo, ShowWithComedian } from "@/data/shows";
import { useEffect, useRef } from "react";

import { Comedian } from "@/data/comedians";
import { WaitlistSubscription } from "@punchup/data/client";

interface ResponsiveShowRowProps {
  shows: ShowInfo[];
  comedian: Comedian;
  showcaseShow?: ShowWithComedian;
  location: string;
  isNearby?: boolean;
  internalReferrer?: string;
  buyTicketsShowId?: string;
  setBuyTicketsShowId?: (id: string) => void;
  highlight?: boolean;
  isFollowed?: boolean;
  venueSlug?: string | null;
  followedComedianIds?: string[];
  hideComedianAvatar?: boolean;
  hideHeatingUp?: boolean;
  hideUsersChecking?: boolean;
  hideComedianName?: boolean;
  pageSection?: PAGE_SECTIONS;
  ctaSource?: CTA_SOURCES;
  // See [[ShowRowContextValue.waitlistSubscription]] — parents that batch
  // waitlist status pass this through to skip ShowRowBuyButton's per-show fetch.
  waitlistSubscription?: WaitlistSubscription | null;
}

/*
 * This component is used to display a row representing 1 or many shows.
 * If there is only 1 show, it will display a single show.
 * If there are multiple shows, it will group the shows into a single row.
 */
export function ResponsiveShowRow({
  highlight = false,
  ...providerProps
}: ResponsiveShowRowProps) {
  const showRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (highlight) {
      setTimeout(() => {
        showRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center",
        });
      }, 300);
    }
  }, [highlight]);

  return (
    <ShowRowProvider {...providerProps}>
      {/* on desktop, render the Show Row */}
      <div className="hidden md:block">
        <ShowRow showRef={showRef} />
      </div>
      {/* on mobile, render the Show Card */}
      <div className="md:hidden">
        <ShowCard showRef={showRef} />
      </div>
    </ShowRowProvider>
  );
}
